import React, { useEffect } from "react";
import { graphql } from 'gatsby';
// components
import SEO from '../components/seo';
import ResponsiveResolutionImage from '@components/responsive-resolution-image/responsive-resolution-image';
// hooks
import { useDirectusData } from '@hooks/directusHooks/useDirectusData';
import { useImage } from '@hooks/useImage';
// styles
import * as styles from './blog-article.module.scss';
//services
import { formatArticleData } from "@vitl-services/services/format";
import { trackEvent, trackHeapEvent, trackPageView } from "@vitl-services/services/analytics/tracking";
// interfaces
import { IArticleData, ISocialLink } from "@vitl-services/interfaces/blog";


interface IProps {
  data:
    | {
        directus: {
          blog_articles_by_id: IArticleData;
        };
      }
    | IArticleData;
  isDataFromAppsync?: boolean;
}

const BlogArticle: React.FC<IProps> = ({ data, isDataFromAppsync = false }) => {
  const articleData = isDataFromAppsync
    ? data
    : data.directus.blog_articles_by_id;

  const formattedData = formatArticleData(articleData, isDataFromAppsync);
  const {
    settingsData: { social_links: socialLinks },
    blogCommonData,
  } = useDirectusData();

  const [logoHeader, iconTwitter, iconFacebook] = useImage([
    'logo_header.svg',
    'icons/icon_twitter_blog.svg',
    'icons/icon_facebook_blog.svg',
  ]);

  const twitterUsername = 'vitl';
  const siteUrl = 'https://vitl.com';

  useEffect(()=>{
    if(formattedData) {
      trackPageView(`blog_${formattedData.name}_article`);
      trackHeapEvent(`blog_${formattedData.name}_article_page_view`);
    }
  },[formattedData]);

  if (!formattedData) return null;
  return (
    <>
      <SEO
        title={formattedData.name}
        description={formattedData.preview || ''}
      />
      <section className={styles.sectionHeader}>
        <div className={styles.container}>
          <div className={styles.row}>
            <div className={styles.contentHeader}>
              <h1 data-testid="article-title">{formattedData.name}</h1>
              <p>
                <strong data-testid="date">
                  {formattedData.author
                    ? `${formattedData.author.name} ${
                        formattedData.date ? `/ ${formattedData.date}` : ''
                      }`
                    : formattedData.date || ''}
                </strong>
              </p>
              <div className={styles.heroImageWrapper}>
                <div
                  className={styles.heroImage}
                  style={{
                    backgroundImage: `url(${formattedData.image}), url(${logoHeader})`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className={styles.container}>
          <div className={styles.row}>
            <div className={styles.articleContentWrapper}>
              <h2 data-testid="article-subtitle">{formattedData.preview}</h2>
              <hr className={styles.hrLeftAligned} />
              <div className={styles.shareButtonsWrapper}>
                Share:
                <div
                  className={styles.shareButtons}
                  data-testid="share-buttons"
                >
                  {
                    <a
                      href={`https://twitter.com/intent/tweet/?text=${formattedData.name}&url=${formattedData.slug}&via=${twitterUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // analytics
                        trackEvent('Social Share', {
                          social_media: 'twitter',
                          content_name: formattedData.name,
                        });
                      }}
                    >
                      <ResponsiveResolutionImage
                        width={15}
                        height={43}
                        url={iconTwitter}
                        title="Twitter logo"
                      />
                    </a>
                  }
                  {
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${siteUrl}/${formattedData.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // analytics
                        trackEvent('Social Share', {
                          social_media: 'facebook',
                          content_name: formattedData.name,
                        });
                      }}
                    >
                      <ResponsiveResolutionImage
                        width={15}
                        height={43}
                        url={iconFacebook}
                        title="Facebook logo"
                      />
                    </a>
                  }
                </div>
              </div>
              <div
                className={styles.articleContent}
                dangerouslySetInnerHTML={{ __html: formattedData.content }}
              />
              {formattedData.author && (
                <div className={styles.articleFooter}>
                  {formattedData.author.image && (
                    <ResponsiveResolutionImage
                      filename={formattedData.author.image}
                      title={'Article author image'}
                      width={90}
                      height={90}
                      heightQp={80}
                    />
                  )}
                  <div className={styles.nameWrap}>
                    <h4>{formattedData.author.name}</h4>
                    <p className={styles.subheading}>
                      {formattedData.author.role}
                    </p>
                    <div className={styles.socialLinks}>
                      {socialLinks?.map((item: ISocialLink, index: number) => (
                        <a
                          key={index}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            // analytics
                            trackEvent('Click CTA', {
                              cta_name: item.name,
                              cta_category: 'social links',
                            });
                          }}
                        >
                          <ResponsiveResolutionImage
                            url={useImage([`icons/icon_${item.name}.svg`])}
                            title={item.name}
                            width={10}
                            height={10}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                  <p
                    className={styles.footerCopy}
                  >{blogCommonData.common_footer_copy}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export const query = graphql`
  query ($id: ID!) {
    directus {
      blog_articles_by_id(id: $id) {
        name
        author {
          name
          role
          image {
            filename_disk
          }
        }
        preview
        content
        date_published
        image {
          filename_disk
        }
      }
    }
  }
`;

export default BlogArticle;
